import { errorHandler } from "@/services/toastHandler";

const initState = () => ({
  loadingMeetings: false,
  loadingRaces: false,
  loadingAddResult: false,
  loadingAbandon: false,
  loadingBets: false,
  paginationBets: {
    total: 0,
    page: 1,
    limit: 10,
  },
  paginationSelections: {
    total: 0,
    page: 1,
    limit: 10,
  },
  loadingSelections: false,
  loadingUpdateRaceDetail: false,
  loadingGetRaceDetail: false,
  dataRaceDetail: null,
  loadingUpdateConfig: false,
  loadingUpdateMeetingOrderding: false,
  loadingDisableMeeting: false,
  loadingListDisabledMeeting: false,
});

export default {
  namespaced: true,
  state: initState(),
  mutations: {
    LOAD_LIST_MEETINGS: (s, data) => (s.loadingMeetings = data),
    LOAD_LIST_RACES: (s, data) => (s.loadingRaces = data),
    LOAD_ADD_RESULT: (s, data) => (s.loadingAddResult = data),
    LOAD_ABANDON: (s, data) => (s.loadingAbandon = data),
    SET_PAGINATION_BETS: (s, data) => (s.paginationBets = data),
    SET_LOADING_BETS: (s, data) => (s.loadingBets = data),
    SET_PAGINATION_SELECTIONS: (s, data) => (s.paginationSelections = data),
    SET_LOADING_SELECTIONS: (s, data) => (s.loadingSelections = data),
    LOAD_UPDATE_RACE: (s, data) => (s.loadingUpdateRaceDetail = data),
    LOAD_GET_RACE_DETAIL: (s, data) => (s.loadingGetRaceDetail = data),
    SET_RACE_DETAIL: (s, data) => (s.dataRaceDetail = data),
    LOAD_UPDATE_RACE_CONFIG_MARKET: (s, data) => (s.loadingUpdateConfig = data),
    LOAD_UPDATE_MEETING_ORDERING: (s, data) =>
      (s.loadingUpdateMeetingOrderding = data),
    LOAD_UPDATE_DISABLE_MEETING: (s, data) => (s.loadingDisableMeeting = data),
    LOAD_LIST_DISABLED_MEETINGS: (s, data) =>
      (s.loadingListDisabledMeeting = data),
  },

  actions: {
    async getListMeetings({ commit }, params) {
      commit("LOAD_LIST_MEETINGS", true);
      try {
        const res = await this.$api.get("/v1/portal/racing/meetings", {
          params,
        });
        return res.data.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOAD_LIST_MEETINGS", false);
      }
    },
    // Uses its own loading state so it never triggers the meeting list skeletons
    async getFutureMeetings(_, params) {
      try {
        const res = await this.$api.get("/v1/portal/racing/meetings/futures", {
          params,
        });
        const data = res.data.data;
        return Array.isArray(data) ? data : data?.data || [];
      } catch (e) {
        errorHandler(e);
        return [];
      }
    },
    // Uses its own loading state so it never triggers the meeting list skeletons
    async searchMeetings(_, params) {
      try {
        const res = await this.$api.get("/v1/portal/racing/meetings/list", {
          params,
        });
        const data = res.data.data;
        return Array.isArray(data) ? data : data?.data || [];
      } catch (e) {
        errorHandler(e);
        return [];
      }
    },
    async getRacesOfMeeting({ commit }, params) {
      commit("LOAD_LIST_RACES", true);
      try {
        const res = await this.$api.get("/v1/portal/racing/races", {
          params: { ...params, limit: 100 },
        });
        return res.data.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOAD_LIST_RACES", false);
      }
    },
    async addResultManual({ commit }, params) {
      commit("LOAD_ADD_RESULT", true);
      try {
        const { raceId, ...rest } = params;
        const res = await this.$api.post(
          `/v1/portal/racing/${raceId}/results`,
          rest,
        );
        if (res.data) {
          await this.$api.post(
            `/v1/portal/racing/${raceId}/recalculate-result`,
          );
        }
        return res.data.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOAD_ADD_RESULT", false);
      }
    },
    async abandonRace({ commit }, raceId) {
      try {
        commit("LOAD_ABANDON", true);
        const { data: res } = await this.$api.put(
          `v1/portal/racing/${raceId}/abandon`,
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOAD_ABANDON", false);
      }
    },
    async abandonMeeting({ commit }, meetingId) {
      try {
        commit("LOAD_ABANDON", true);
        const { data: res } = this.$api.put(
          `v1/portal/racing/meetings/${meetingId}/abandon`,
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOAD_ABANDON", false);
      }
    },
    async updateRaceDetail({ commit }, payload) {
      try {
        commit("LOAD_UPDATE_RACE", true);
        const { raceId, ...rest } = payload;
        const { data: res } = await this.$api.put(
          `/v1/portal/racing/${raceId}`,
          rest,
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOAD_UPDATE_RACE", false);
      }
    },
    async getRunnersOfRace(_, raceId) {
      try {
        const { data: res } = await this.$api.get(
          `/v1/portal/racing/${raceId}/runner`,
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      }
    },
    async updateRunnersOfRace(_, payload) {
      try {
        const { raceId, runners } = payload;
        const { data: res } = await this.$api.put(
          `/v1/portal/racing/${raceId}/runner`,
          { runners },
        );
        return res?.data ?? true;
      } catch (e) {
        errorHandler(e);
      }
    },
    async getBetsOfRace({ commit }, payload) {
      try {
        commit("SET_LOADING_BETS", true);
        const { raceId, ...restPayload } = payload;
        const { data: res } = await this.$api.get(
          `/v1/racing/races/${raceId}/bet`,
          {
            params: { ...restPayload },
          },
        );
        commit("SET_PAGINATION_BETS", res.pagination);
        return res.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("SET_LOADING_BETS", false);
      }
    },
    async getBetsOfSelection({ commit }, payload) {
      try {
        commit("SET_LOADING_SELECTIONS", true);
        const { selectionId, ...restPayload } = payload;
        const { data: res } = await this.$api.get(
          `/v1/racing/selection/${selectionId}/bet`,
          {
            params: { ...restPayload },
          },
        );
        commit("SET_PAGINATION_SELECTIONS", res.pagination);
        return res.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("SET_LOADING_SELECTIONS", false);
      }
    },
    async updateMeeting({ commit }, payload) {
      try {
        const { meetingId, ...rest } = payload;
        const { data: res } = await this.$api.put(
          `/v1/portal/racing/meetings/${meetingId}`,
          rest,
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      }
    },
    async updateRaceResults({ commit }, payload) {
      try {
        const { raceId, results, dividends } = payload;
        const { data: res } = await this.$api.post(
          `/v1/portal/racing/${raceId}/results`,
          { results, dividends },
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      }
    },
    async recalculateRaceResults({ commit }, raceId) {
      try {
        const { data: res } = await this.$api.post(
          `/v1/portal/racing/${raceId}/recalculate-result`,
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      }
    },
    async getRaceByRangeTime({ commit }, params) {
      try {
        const { data: res } = await this.$api.get(
          "/v1/portal/racing/races/range-time/list",
          {
            params,
          },
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      }
    },
    async getRunnersOfRaceWithHold(_, raceId) {
      try {
        const { data: res } = await this.$api.get(
          `/v1/portal/racing/races/${raceId}/runner`,
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      }
    },
    async getDetailOfRace({ commit }, raceId) {
      commit("LOAD_GET_RACE_DETAIL", true);
      try {
        const res = await this.$api.get(`/v1/portal/racing/races/${raceId}`);

        commit("SET_RACE_DETAIL", res.data.data);
        return res.data.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOAD_GET_RACE_DETAIL", false);
      }
    },
    async updateRaceConfigMarket({ commit }, payload) {
      commit("LOAD_UPDATE_RACE_CONFIG_MARKET", true);
      try {
        const { raceId, ...data } = payload;
        const res = await this.$api.put(
          `/v1/portal/racing/races/${raceId}/advance`,
          data,
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOAD_UPDATE_RACE_CONFIG_MARKET", false);
      }
    },
    async updateMeetingOrdering({ commit }, payload) {
      commit("LOAD_UPDATE_MEETING_ORDERING", true);
      try {
        const res = await this.$api.put(
          "/v1/portal/racing/meetings/order-number",
          payload,
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOAD_UPDATE_MEETING_ORDERING", false);
      }
    },
    async disableMeeting({ commit }, payload) {
      commit("LOAD_UPDATE_DISABLE_MEETING", true);
      try {
        const res = await this.$api.post(
          "/v1/portal/racing/ignored-meetings",
          payload,
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOAD_UPDATE_DISABLE_MEETING", false);
      }
    },
    async enableMeeting({ commit }, payload) {
      commit("LOAD_UPDATE_DISABLE_MEETING", true);
      try {
        const res = await this.$api.post(
          `/v1/portal/racing/ignored-meetings/delete`,
          payload
        );
        return res.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOAD_UPDATE_DISABLE_MEETING", false);
      }
    },
    async getAllListDisabledMeeting({ commit }) {
      commit("LOAD_LIST_DISABLED_MEETINGS", true);
      try {
        const res = await this.$api.get("/v1/portal/racing/ignored-meetings");
        return res.data.data;
      } catch (e) {
        errorHandler(e);
      } finally {
        commit("LOAD_LIST_DISABLED_MEETINGS", false);
      }
    },
  },
};
